import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { loadAccess, MenuEntry, useAccess } from '../auth/access';
import { useAuth } from '../modules/auth/auth.context';
import { AppLogo } from '../modules/shared/components';

/** Pages that live under another sub-menu's path family, so it stays lit on them. */
const EXTRA_PATHS: Record<string, string[]> = {
  'projects.projects': ['/jobs'],
  'projects.site-visits': ['/work/site-visits'],
  'billing.payments': ['/credits', '/debits'],
};

interface Module {
  code: string;
  label: string;
  icon: string;
  items: MenuEntry[];
}

const OPEN_KEY = 'sidebar.open';

function readOpen(): string[] {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]');
  } catch {
    return [];
  }
}

/** The "Dashboard" entry a module group opens with; not a granted menu, so not in `/api/my/access`. */
function dashboardEntry(mod: Module): MenuEntry {
  return {
    code: `${mod.code}.__dashboard`,
    moduleCode: mod.code,
    moduleLabel: mod.label,
    moduleIcon: mod.icon,
    moduleSort: 0,
    label: 'Dashboard',
    path: `/dashboards/${mod.code}`,
    icon: 'mdi:view-dashboard-variant-outline',
    sortOrder: -1,
    actions: ['VIEW'],
    adminOnly: false,
    description: null,
  };
}

/** The sub-menu whose path is the longest prefix of the current page. */
function activeCode(pathname: string, menus: MenuEntry[]): string | null {
  let best: { code: string; len: number } | null = null;
  for (const m of menus) {
    for (const p of [m.path, ...(EXTRA_PATHS[m.code] ?? [])]) {
      const hit = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(`${p}/`);
      if (hit && (!best || p.length > best.len)) best = { code: m.code, len: p.length };
    }
  }
  return best?.code ?? null;
}

/**
 * The role-wise menu (V30): main menus (modules) with their sub-menus, exactly as granted to the
 * signed-in user's role by `/api/my/access`. Home's entries sit at the top ungrouped; every
 * other module folds open and shut, and the one holding the current page opens itself.
 */
export default function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, loading } = useAuth();
  const access = useAccess();
  const sidebarRef = useRef(null);

  useEffect(() => {
    loadAccess().catch(() => undefined);
  }, []);

  const menus = useMemo(() => access?.menus ?? [], [access]);
  const modules = useMemo(() => {
    const byCode = new Map<string, Module>();
    [...menus]
      .sort((a, b) => a.moduleSort - b.moduleSort || a.sortOrder - b.sortOrder)
      .forEach((m) => {
        if (!byCode.has(m.moduleCode))
          byCode.set(m.moduleCode, { code: m.moduleCode, label: m.moduleLabel, icon: m.moduleIcon, items: [] });
        byCode.get(m.moduleCode)!.items.push(m);
      });
    // Every module but Home opens with its dashboard (`/dashboards/<module>`).
    byCode.forEach((mod) => {
      if (mod.code !== 'home') mod.items.unshift(dashboardEntry(mod));
    });
    return [...byCode.values()];
  }, [menus]);

  // The dashboard entries take part in the longest-prefix match like any granted sub-menu.
  const navEntries = useMemo(() => modules.flatMap((m) => m.items), [modules]);
  const active = activeCode(pathname, navEntries);
  const activeModule = navEntries.find((m) => m.code === active)?.moduleCode;

  const [open, setOpen] = useState<string[]>(readOpen);
  useEffect(() => {
    if (activeModule && !open.includes(activeModule)) setOpen((o) => [...o, activeModule]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModule]);
  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify(open));
    } catch {
      /* memory only */
    }
  }, [open]);
  const toggle = (code: string) => setOpen((o) => (o.includes(code) ? o.filter((c) => c !== code) : [...o, code]));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !sidebarRef.current) return;

    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const sidebarWidth = sidebarRef.current.offsetWidth;
        document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth + 48}px`); // 48px for drawer paper padding
      }, 100);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Drawer
      variant='permanent'
      anchor='left'
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          overflowX: 'hidden',
          border: 'none',
          boxShadow: 'none',
          padding: '16px 24px',
          width: 'auto',
          position: 'fixed',
          height: '100vh',
          overflow: 'auto',
          transform: 'none',
          transition: 'width 0.3s ease-in-out',
        },
      }}
    >
      <Stack
        ref={sidebarRef}
        gap={2}
        sx={{
          width: 'fit-content',
          transition: 'all 0.3s ease-in-out',
          flex: 1,
        }}
      >
        <Stack
          sx={{
            padding: '8px 0 24px',
            borderBottom: '1px solid',
            borderBottomColor: 'grey.300',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack
            sx={{
              padding: '12px',
              borderRadius: 2,
              backgroundColor: 'primary.contrastText',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <AppLogo size='md' orientation='horizontal' />
          </Stack>
        </Stack>

        <Toolbar disableGutters sx={{ flexDirection: 'column', flex: 1, gap: 3, width: '100%' }}>
          {access && menus.length === 0 && (
            <Typography sx={{ fontSize: '0.875rem', opacity: 0.8, padding: '0 12px', maxWidth: 220 }}>
              Your login has no menu yet. Ask an admin to give it a role.
            </Typography>
          )}
          {modules.map((module) =>
            module.code === 'home' ? (
              <Stack key={module.code} gap={1} sx={{ width: '100%' }}>
                {module.items.map((item) => (
                  <SidebarItem key={item.code} item={item} active={item.code === active} />
                ))}
              </Stack>
            ) : (
              <Stack key={module.code} gap={0.5} sx={{ width: '100%' }}>
                <Button
                  onClick={() => toggle(module.code)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '8px 12px',
                    width: '100%',
                    gap: '16px',
                    borderRadius: '8px',
                    color: 'primary.contrastText',
                    textTransform: 'none',
                    fontWeight: module.code === activeModule ? 700 : 600,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
                  }}
                >
                  <Icon icon={module.icon} width={18} />
                  <Typography sx={{ whiteSpace: 'nowrap', fontSize: '1rem', fontWeight: 'inherit', flex: 1, textAlign: 'left' }}>
                    {module.label}
                  </Typography>
                  <Icon icon={open.includes(module.code) ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={18} />
                </Button>
                <Collapse in={open.includes(module.code)} unmountOnExit>
                  <Stack gap={0.5} sx={{ paddingLeft: '14px', borderLeft: '1px solid rgba(255,255,255,0.25)', marginLeft: '20px' }}>
                    {module.items.map((item) => (
                      <SidebarItem key={item.code} item={item} active={item.code === active} />
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
            )
          )}
        </Toolbar>

        <Stack gap={2} sx={{ marginTop: 'auto' }}>
          {/* Available to every signed-in role: the endpoint checks the old password,
              so it can only ever change the account the user already holds. */}
          <Button
            variant='contained'
            onClick={() => navigate('/reset-password')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '8px 12px',
              width: '100%',
              gap: '16px',
              backgroundColor: 'transparent',
              borderRadius: '8px',
              color: 'primary.contrastText',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.3s ease-in-out',
              textAlign: 'left',
              fontSize: '1rem',
              '&:hover': { backgroundColor: 'primary.contrastText', color: 'primary.main' },
            }}
          >
            <Icon icon='mdi:form-textbox-password' width={18} />
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                transform: 'translateX(0)',
                textAlign: 'left',
                fontSize: '1rem',
              }}
            >
              Change Password
            </Typography>
          </Button>

          <Button
            variant='contained'
            disabled={loading}
            onClick={handleLogout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '8px 12px',
              width: '100%',
              gap: '16px',
              backgroundColor: 'transparent',
              borderRadius: '8px',
              color: 'primary.contrastText',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.3s ease-in-out',
              textAlign: 'left',
              fontSize: '1rem',
              '&:hover': { backgroundColor: 'primary.contrastText', color: 'primary.main' },
            }}
          >
            <Icon icon='mdi:logout' width={18} />
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                transform: 'translateX(0)',
                textAlign: 'left',
                fontSize: '1rem',
              }}
            >
              Logout
            </Typography>
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

function SidebarItem({ item, active }: { item: MenuEntry; active: boolean }) {
  return (
    <MuiLink
      component={NavLink}
      to={item.path}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '7px 12px',
        width: '100%',
        gap: '14px',
        backgroundColor: active ? 'primary.contrastText' : 'transparent',
        borderRadius: '8px',
        color: active ? 'primary.main' : 'primary.contrastText',
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'all 0.3s ease-in-out',
        textAlign: 'left',
        fontSize: '0.95rem',
        '&:visited': { color: active ? 'primary.main' : 'primary.contrastText' },
        '&:hover': { backgroundColor: 'primary.contrastText', color: 'primary.main' },
      }}
    >
      <Icon icon={item.icon} width={17} />
      <Typography sx={{ whiteSpace: 'nowrap', textAlign: 'left', fontSize: 'inherit' }}>{item.label}</Typography>
    </MuiLink>
  );
}
